import { useEffect, useMemo, useState } from 'react';
import { UpdateReport } from 'src/api';
import { Avatar, Button, Link, Switch } from 'src/components/atoms';
import {
  handleWarningToast,
  InformationBanner,
  Modal,
} from 'src/components/molecules';
import { UserType } from 'src/graphql';
import { useIsFeatureEnabled } from 'src/hooks';
import { useSearchUsers } from 'src/hooks/users/useSearchUsers';
import { SYSTEM_PERMISSIONS } from 'src/permissionsTypes';
import * as Types from 'src/types';
import { FeatureFlag, FormState } from 'src/types';
import { getInitials } from 'src/utils/getInitials';
import {
  BannerWrap,
  Body,
  CurrentCmName,
  CurrentCmRow,
  DescriptionWithLink,
  Footer,
  InstructionText,
  PickerSection,
  SectionLabel,
  ToggleBlock,
  ToggleLabel,
  ToggleRow,
} from './AppointCaseManagerModal.styles';
import {
  appointCaseManagerModalMessages,
  caseManagerInstructionWhenOther,
} from './appointCaseManagerModalMessages';
import { CaseManagerDirectoryPicker } from './CaseManagerDirectoryPicker';

const appointCMmodalMessages = appointCaseManagerModalMessages;

export interface AppointCaseManagerModalResult {
  assignAutomatically: boolean;
  selectedUser: UserType | null;
}

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  currentReport: UpdateReport | null;
  currentUser: Types.User;
  onConfirm: (result: AppointCaseManagerModalResult) => void;
  isSubmitting?: boolean;
  'data-testid'?: string;
}

export const AppointCaseManagerModal = ({
  open,
  setOpen,
  currentReport,
  currentUser,
  onConfirm,
  isSubmitting = false,
  ...props
}: Props) => {
  const testId = props['data-testid'] ?? 'appoint-case-manager-modal';
  const isRoutingEnabled = useIsFeatureEnabled(FeatureFlag.ROUTING);
  const [assignAutomatically, setAssignAutomatically] = useState(false);

  const currentUserIsReporter = currentReport?.user === currentUser?.id;
  const preselectedUser = useMemo((): UserType | null => {
    if (
      currentUser?.systemPermissions?.includes(
        SYSTEM_PERMISSIONS.ASSIGNABLE_TO_CASE_TEAM,
      ) &&
      currentUser?.systemPermissions?.includes(
        SYSTEM_PERMISSIONS.ASSIGNABLE_AS_CASE_LEAD,
      ) &&
      !currentUserIsReporter
    ) {
      return currentUser as UserType;
    }
    return null;
  }, [currentUser, currentUserIsReporter]);

  const internalPerpetrators = currentReport?.perpetrators
    ?.filter((perpetrator) => !perpetrator.external)
    .map((perpetrator) => parseInt(String(perpetrator.id), 10));

  const usersToExclude = useMemo(() => {
    const list: (number | undefined)[] = [];
    if (currentReport?.assignee) {
      list.push(currentReport.assignee);
    }
    if (internalPerpetrators?.length) {
      list.push(...internalPerpetrators);
    }
    if (currentReport?.User?.id) {
      list.push(currentReport.User.id);
    }
    return list;
  }, [currentReport?.assignee, currentReport?.User?.id, internalPerpetrators]);

  const { selectedUser, setSelectedUser, searchUsers, sortedValidUsers } =
    useSearchUsers(
      [],
      usersToExclude,
      false,
      true,
      preselectedUser,
      true,
      true,
    );

  useEffect(() => {
    if (open) {
      setAssignAutomatically(false);
      setSelectedUser(preselectedUser);
      searchUsers('');
    }
  }, [
    open,
    searchUsers,
    currentReport?.assignee,
    currentReport?.assigneeName,
    setSelectedUser,
    preselectedUser,
  ]);

  const caseLeadId = selectedUser?.id;

  const isSelfCaseManager =
    caseLeadId != null && Number(currentUser?.id) === Number(caseLeadId);

  const displayName = useMemo(() => {
    if (isSelfCaseManager) {
      return `${currentUser?.name ?? ''}${appointCMmodalMessages.youSuffix}`;
    }
    const name =
      selectedUser?.displayName?.trim() ?? selectedUser?.name?.trim();
    if (name) return name;
    return '—';
  }, [selectedUser, currentUser?.name, isSelfCaseManager]);

  const managerNameForInstruction = isSelfCaseManager
    ? (currentUser?.name ?? '')
    : selectedUser?.name?.trim() || displayName;

  const instruction = isSelfCaseManager
    ? appointCMmodalMessages.instructionWhenSelf
    : caseManagerInstructionWhenOther(managerNameForInstruction);

  const showAccessWarning =
    !assignAutomatically &&
    selectedUser?.eid !== currentUser?.eid &&
    !currentUser?.systemPermissions?.includes(
      SYSTEM_PERMISSIONS.EDIT_ALL_CASES,
    );

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    if (assignAutomatically) {
      onConfirm({ assignAutomatically: true, selectedUser: null });
      return;
    }
    if (!selectedUser) {
      handleWarningToast({
        toastMessage: appointCMmodalMessages.selectCaseManagerToast,
      });
      return;
    }
    onConfirm({ assignAutomatically: false, selectedUser });
  };

  const confirmDisabled = isSubmitting;

  const nameForInitials =
    displayName.replace(appointCMmodalMessages.youSuffix, '').trim() ||
    selectedUser?.name;
  const initials = getInitials(nameForInitials);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      size="large"
      title={appointCMmodalMessages.title}
      hideButtons
      onCancel={handleClose}
      data-testid={testId}
    >
      <Body>
        {!assignAutomatically && (
          <>
            <div>
              <SectionLabel>
                {appointCMmodalMessages.currentCaseManager}
              </SectionLabel>
              <CurrentCmRow>
                <Avatar
                  variant={initials ? 'text' : 'icon'}
                  size="medium"
                  text={initials}
                  data-testid={`${testId}-current-cm-avatar`}
                />
                <CurrentCmName data-testid={`${testId}-current-cm-name`}>
                  {displayName}
                </CurrentCmName>
              </CurrentCmRow>
              {isSelfCaseManager && (
                <InstructionText>{instruction}</InstructionText>
              )}
            </div>

            <PickerSection data-testid={`${testId}-picker-section`}>
              <SectionLabel>{appointCMmodalMessages.caseManagers}</SectionLabel>
              <CaseManagerDirectoryPicker
                sortedValidUsers={sortedValidUsers}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                searchUsers={searchUsers}
                placeholder={appointCMmodalMessages.searchPlaceholder}
                visible={open}
                testIdPrefix={testId}
              />
              {showAccessWarning && (
                <BannerWrap>
                  <InformationBanner
                    data-testid={`${testId}-access-warning`}
                    state={FormState.WARNING}
                    title={appointCMmodalMessages.accessWarningTitle}
                    description={
                      appointCMmodalMessages.accessWarningDescription
                    }
                  />
                </BannerWrap>
              )}
            </PickerSection>
          </>
        )}

        <ToggleBlock data-testid={`${testId}-auto-assign`}>
          <ToggleRow>
            <Switch
              checked={assignAutomatically}
              onClick={setAssignAutomatically}
              data-testid={`${testId}-assign-automatically-switch`}
            />
            <ToggleLabel>
              {appointCMmodalMessages.assignAutomatically}
            </ToggleLabel>
          </ToggleRow>
          <DescriptionWithLink>
            {appointCMmodalMessages.assignAutomaticallyDescriptionPrefix}{' '}
            {isRoutingEnabled ? (
              <Link to="/routing" data-testid={`${testId}-routing-link`}>
                {appointCMmodalMessages.routingPageLink}
              </Link>
            ) : (
              appointCMmodalMessages.routingPageLink
            )}
            {appointCMmodalMessages.assignAutomaticallyDescriptionSuffix}
          </DescriptionWithLink>
        </ToggleBlock>
      </Body>

      <Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          data-testid={`${testId}-cancel`}
        >
          {appointCMmodalMessages.cancel}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={confirmDisabled}
          loading={isSubmitting}
          data-testid={`${testId}-confirm`}
        >
          {appointCMmodalMessages.confirmSelection}
        </Button>
      </Footer>
    </Modal>
  );
};
